import React, { useEffect, useRef, useState } from "react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@coval/ui"
import { ChevronDown, Eye, EyeOff, RotateCcw, Trash2 } from "lucide-react"

import { OBJECT_VIEW_MODE, type ObjectViewMode } from "@/lib/viewModes"
import type { JsonObject } from "@/types/json"

import { useTestSetEditorContext } from "../../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../../event-bus"
import type { MetadataColumn } from "../../hooks/useMetadataColumns"
import {
  METADATA_COLUMN_TYPE_LABELS,
  type MetadataColumnType,
} from "../../utils/metadataColumnType"

const METADATA_COLUMN_WIDTH = "240px"

const TYPE_OPTIONS: MetadataColumnType[] = [
  "string",
  "int",
  "float",
  "bool",
  "list",
  "dict",
]

interface HeaderContextMenuState {
  x: number
  y: number
}

interface HeaderContextMenuProps {
  position: HeaderContextMenuState
  metadataViewMode: ObjectViewMode
  onSetViewMode: (mode: ObjectViewMode) => void
  hiddenColumns: MetadataColumn[]
  onShowColumn: (key: string) => void
  onClose: () => void
}

const HeaderContextMenu: React.FC<HeaderContextMenuProps> = ({
  position,
  metadataViewMode,
  onSetViewMode,
  hiddenColumns,
  onShowColumn,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handlePointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }
    document.addEventListener("mousedown", handlePointer)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handlePointer)
      document.removeEventListener("keydown", handleKey)
    }
  }, [onClose])

  return (
    <div
      ref={ref}
      role="menu"
      aria-label="Metadata view options"
      tabIndex={-1}
      className="fixed z-50 min-w-52 rounded-md border border-coval-border bg-surface-0 shadow-md py-1 text-sm"
      style={{ top: position.y, left: position.x }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="px-3 py-1 text-xs font-medium text-coval-text-muted">
        View metadata as
      </div>
      {(
        [
          [OBJECT_VIEW_MODE.COLUMNS, "Columns"],
          [OBJECT_VIEW_MODE.KEY_VALUE, "List"],
          [OBJECT_VIEW_MODE.JSON, "Collapsed"],
        ] as const
      ).map(([mode, label]) => (
        <button
          key={mode}
          type="button"
          role="menuitem"
          onClick={() => {
            onSetViewMode(mode)
            onClose()
          }}
          className={`flex items-center w-full px-3 py-1.5 text-left hover:bg-surface-1 ${
            metadataViewMode === mode
              ? "text-coval-contrast"
              : "text-coval-text"
          }`}
        >
          {label}
        </button>
      ))}
      {hiddenColumns.length > 0 ? (
        <>
          <div className="my-1 border-t border-coval-border" />
          <div className="px-3 py-1 text-xs font-medium text-coval-text-muted">
            Show hidden columns
          </div>
          {hiddenColumns.map((column) => (
            <button
              key={column.key}
              type="button"
              role="menuitem"
              onClick={() => {
                onShowColumn(column.key)
                onClose()
              }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-left text-coval-text hover:bg-surface-1"
            >
              <Eye className="w-4 h-4 text-coval-text-muted" />
              <span className="truncate">{column.key}</span>
            </button>
          ))}
        </>
      ) : null}
    </div>
  )
}

interface MetadataColumnHeaderProps {
  column: MetadataColumn
  effectiveType: MetadataColumnType
  isOverridden: boolean
  onOverride: (type: MetadataColumnType) => void
  onClearOverride: () => void
  onHide: () => void
  onDelete: () => void
  onContextMenu: (e: React.MouseEvent) => void
}

/**
 * Single metadata column header. Left-click opens the type/hide dropdown;
 * right-click opens the shared view-mode context menu (handled by the parent).
 */
const MetadataColumnHeader: React.FC<MetadataColumnHeaderProps> = ({
  column,
  effectiveType,
  isOverridden,
  onOverride,
  onClearOverride,
  onHide,
  onDelete,
  onContextMenu,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <th
        className="relative py-3 text-left text-sm px-2 font-medium text-coval-text !bg-surface-default cursor-pointer transition-colors hover:bg-coval-contrast/10 data-pressed:opacity-100 aria-expanded:opacity-100"
        style={{
          width: METADATA_COLUMN_WIDTH,
          minWidth: METADATA_COLUMN_WIDTH,
          verticalAlign: "middle",
        }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={onContextMenu}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <span className="truncate">{column.key}</span>
            <span className="text-coval-text-muted font-normal text-xs shrink-0">
              {METADATA_COLUMN_TYPE_LABELS[effectiveType]}
              {isOverridden ? "*" : ""}
            </span>
          </span>
          <ChevronDown className="w-4 h-4 text-coval-text-muted shrink-0" />
        </span>
      </th>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      <DropdownMenuLabel>Type</DropdownMenuLabel>
      {TYPE_OPTIONS.map((type) => (
        <DropdownMenuItem
          key={type}
          onSelect={() => onOverride(type)}
          className={type === effectiveType ? "text-coval-contrast" : ""}
        >
          {METADATA_COLUMN_TYPE_LABELS[type]}
          {type === column.inferredType && !isOverridden ? " (inferred)" : ""}
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      {isOverridden ? (
        <>
          <DropdownMenuItem onSelect={onClearOverride} className="gap-2">
            <RotateCcw className="w-4 h-4" />
            Reset to inferred (
            {METADATA_COLUMN_TYPE_LABELS[column.inferredType]})
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      ) : null}
      <DropdownMenuItem onSelect={onHide} className="gap-2">
        <EyeOff className="w-4 h-4" />
        Hide column
      </DropdownMenuItem>
      <DropdownMenuItem destructive onSelect={onDelete} className="gap-2">
        <Trash2 className="w-4 h-4" />
        Delete column
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

export const MetadataColumnHeaders: React.FC = () => {
  const {
    metadataColumns,
    metadataColumnState,
    setMetadataViewMode,
    metadataViewMode,
    testCases,
  } = useTestSetEditorContext()
  const dispatch = useEventDispatcher()

  const {
    overrides,
    hiddenKeys,
    setOverride,
    clearOverride,
    hideColumn,
    showColumn,
    removeAddedColumn,
  } = metadataColumnState

  const visibleColumns = metadataColumns.filter((c) => !hiddenKeys.has(c.key))
  const hiddenColumns = metadataColumns.filter((c) => hiddenKeys.has(c.key))

  const [ctxMenu, setCtxMenu] = useState<HeaderContextMenuState | null>(null)

  const openContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setCtxMenu({ x: e.clientX, y: e.clientY })
  }

  const deleteColumn = (key: string) => {
    // Drop the key from any test case that currently carries it.
    for (const tc of testCases) {
      const meta = tc.expected_output_json
      if (!meta || !(key in meta)) {
        continue
      }
      const next: JsonObject = { ...meta }
      delete next[key]
      dispatch(EVENTS.TEST_CASE_UPDATE, {
        testCaseId: tc.id,
        updates: { expected_output_json: next },
      })
    }
    removeAddedColumn(key)
    if (key in overrides) {
      clearOverride(key)
    }
    if (hiddenKeys.has(key)) {
      showColumn(key)
    }
  }

  if (visibleColumns.length === 0) {
    return null
  }

  return (
    <>
      {visibleColumns.map((column) => {
        const override = overrides[column.key]
        const effectiveType = override ?? column.inferredType
        return (
          <MetadataColumnHeader
            key={column.key}
            column={column}
            effectiveType={effectiveType}
            isOverridden={Boolean(override)}
            onOverride={(type) => setOverride(column.key, type)}
            onClearOverride={() => clearOverride(column.key)}
            onHide={() => hideColumn(column.key)}
            onDelete={() => deleteColumn(column.key)}
            onContextMenu={openContextMenu}
          />
        )
      })}
      {ctxMenu ? (
        <HeaderContextMenu
          position={ctxMenu}
          metadataViewMode={metadataViewMode}
          onSetViewMode={setMetadataViewMode}
          hiddenColumns={hiddenColumns}
          onShowColumn={showColumn}
          onClose={() => setCtxMenu(null)}
        />
      ) : null}
    </>
  )
}

export { METADATA_COLUMN_WIDTH }
